/**
 * Owner Branches — table + drawer-based CRUD.
 *
 * Soft delete via DELETE; reactivate via PATCH is_active=true.
 * Image uploads supported in the photos[] array via ImageUploader.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, MapPin, Phone, Edit, Trash2, RefreshCcw, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { assertBrandMatch } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import Drawer from '@/components/ui_primitives/Drawer';
import { FieldRow, Input, Textarea } from '@/components/ui_primitives/Field';
import ImageUploader from '@/components/ui_primitives/ImageUploader';
import { confirm } from '@/components/ui_primitives/confirm';

const schema = z.object({
  name: z.string().min(1, 'Required').max(120),
  address: z.string().min(1, 'Required').max(500),
  city: z.string().min(1, 'Required').max(120),
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  phone: z.string().min(1, 'Required').max(40),
  opening_hours: z.string().min(1, 'Required').max(120),
});

export default function OwnerBranchesPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | 'new' | branch object

  const branchesQ = useQuery({
    queryKey: qk.ownerBranches(brandId),
    queryFn: () => api.ownerBranches(token),
    enabled: !!token && !!brandId,
  });

  const close = () => setEditing(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.ownerBranches(brandId) });

  const remove = useMutation({
    mutationFn: (id) => api.ownerDeleteBranch(token, id),
    onSuccess: () => { toast.success('Branch deactivated'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Delete failed')),
  });
  const reactivate = useMutation({
    mutationFn: (id) => api.ownerPatchBranch(token, id, { is_active: true }),
    onSuccess: () => { toast.success('Branch reactivated'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Reactivate failed')),
  });

  if (branchesQ.isLoading) return <FullSpinner />;
  if (branchesQ.error) return <QueryError error={branchesQ.error} onRetry={() => branchesQ.refetch()} />;

  const items = branchesQ.data || [];
  const active = items.filter((b) => b.is_active !== false);
  const inactive = items.filter((b) => b.is_active === false);

  return (
    <div className="space-y-6" data-testid="owner-branches-page">
      <PageHeader
        kicker="Branches"
        title="Manage your locations"
        subtitle="Add new branches, edit details, soft-delete or reactivate."
        actions={
          <Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')} testID="branch-add">
            Add branch
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState
          icon={<MapPin size={20} />}
          title="No branches yet"
          subtitle="Add your first location to start onboarding cashiers and accepting transactions."
          action={
            <Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')}>Add your first branch</Button>
          }
          testID="branches-empty"
        />
      ) : (
        <SectionCard padded={false}>
          <Table testID="branches-table">
            <THead>
              <Tr>
                <Th>Branch</Th>
                <Th>City</Th>
                <Th>Phone</Th>
                <Th>Hours</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {[...active, ...inactive].map((b) => (
                <Tr key={b.id}>
                  <Td>
                    <div className="flex items-center gap-3">
                      {b.photos?.[0] ? (
                        <img src={resolveImageUrl(b.photos[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-white/30">
                          <ImageIcon size={14} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{b.name}</div>
                        <div className="text-xs text-white/40 truncate">{b.address}</div>
                      </div>
                    </div>
                  </Td>
                  <Td>{b.city}</Td>
                  <Td><span className="font-mono text-xs">{b.phone}</span></Td>
                  <Td>{b.opening_hours}</Td>
                  <Td>
                    {b.is_active === false ? (
                      <StatusPill variant="slate">Inactive</StatusPill>
                    ) : (
                      <StatusPill variant="gold">Active</StatusPill>
                    )}
                  </Td>
                  <Td align="right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="subtle" leftIcon={<Edit size={12} />} onClick={() => setEditing(b)} testID={`branch-edit-${b.id}`}>Edit</Button>
                      {b.is_active === false ? (
                        <Button size="sm" variant="secondary" leftIcon={<RefreshCcw size={12} />}
                          onClick={() => reactivate.mutate(b.id)} loading={reactivate.isPending}
                          testID={`branch-reactivate-${b.id}`}
                        >Reactivate</Button>
                      ) : (
                        <Button size="sm" variant="danger" leftIcon={<Trash2 size={12} />}
                          onClick={async () => {
                            const ok = await confirm({
                              title: `Deactivate ${b.name}?`,
                              message: 'The branch is hidden from customers but its transaction history is preserved.',
                              confirmText: 'Deactivate', tone: 'danger',
                            });
                            if (ok) remove.mutate(b.id);
                          }}
                          testID={`branch-delete-${b.id}`}
                        >Deactivate</Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </SectionCard>
      )}

      {editing ? (
        <BranchForm
          branch={editing === 'new' ? null : editing}
          onClose={close}
          onSaved={() => { invalidate(); close(); }}
        />
      ) : null}
    </div>
  );
}

function BranchForm({ branch, onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const isEdit = !!branch;
  const [photo, setPhoto] = useState(branch?.photos?.[0] ?? '');

  const {
    register, handleSubmit, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: branch?.name ?? '',
      address: branch?.address ?? '',
      city: branch?.city ?? '',
      lat: branch?.lat ?? '',
      lng: branch?.lng ?? '',
      phone: branch?.phone ?? '',
      opening_hours: branch?.opening_hours ?? '',
    },
  });

  const onSubmit = async (vals) => {
    try {
      const body = { ...vals, photos: photo ? [photo] : [] };
      if (isEdit) await api.ownerPatchBranch(token, branch.id, body);
      else await api.ownerCreateBranch(token, body);
      toast.success(isEdit ? 'Branch updated' : 'Branch created');
      onSaved();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Save failed'));
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={isEdit ? 'Edit branch' : 'Add branch'}
      subtitle="Branches belong to your brand only."
      testID="branch-drawer"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting} testID="branch-save">
            {isEdit ? 'Save changes' : 'Create branch'}
          </Button>
        </>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <FieldRow label="Photo" hint="Used in branch cards and the locations list">
          <ImageUploader value={photo} onChange={setPhoto} aspect="card" />
        </FieldRow>
        <FieldRow label="Branch name" required error={errors.name?.message}>
          <Input {...register('name')} placeholder="BSD, Senopati, …" />
        </FieldRow>
        <FieldRow label="Address" required error={errors.address?.message}>
          <Textarea rows={2} {...register('address')} placeholder="Jl. ..." />
        </FieldRow>
        <div className="grid grid-cols-3 gap-3">
          <FieldRow label="City" required error={errors.city?.message}>
            <Input {...register('city')} placeholder="Jakarta" />
          </FieldRow>
          <FieldRow label="Lat" required error={errors.lat?.message}>
            <Input type="number" step="any" {...register('lat')} placeholder="-6.302" />
          </FieldRow>
          <FieldRow label="Lng" required error={errors.lng?.message}>
            <Input type="number" step="any" {...register('lng')} placeholder="106.654" />
          </FieldRow>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Phone" required error={errors.phone?.message}>
            <Input {...register('phone')} placeholder="+62 21-xxxx" />
          </FieldRow>
          <FieldRow label="Opening hours" required error={errors.opening_hours?.message}>
            <Input {...register('opening_hours')} placeholder="10:00 – 22:00 daily" />
          </FieldRow>
        </div>
      </form>
    </Drawer>
  );
}
